import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Divider,
  Alert
} from '@mui/material';
import { Add as AddIcon, Category as CategoryIcon, SubdirectoryArrowRight as SubCategoryIcon } from '@mui/icons-material';
import { get, isEmpty } from 'lodash';
import { ExpenseCategory } from '../../utils/expense.types';
import { useAppDispatch } from '../../store/hooks';
import { createExpenseCategory } from '../../store/expenseStore/expenseThunk';

export interface IGroupedCategoryOption {
  label: string;
  value: string;
  color?: string;
  icon?: string;
  description?: string;
  isSubcategory: boolean;
  parentCategory?: string;
}

export interface ICategoryGroup {
  groupName: string;
  color?: string;
  categories: IGroupedCategoryOption[];
}

interface IGroupedCategorySelectProps {
  name: string;
  label: string;
  categories: ExpenseCategory[];
  id: string;
  required?: boolean;
  selectedValue?: string;
  showNoneLabel?: boolean;
  allowCreate?: boolean; // Enable category creation
  isFilter?: boolean; // If true, adapts for filter usage
  onCategoryCreated?: (category: ExpenseCategory) => void; // Callback after creation
  [x: string]: any;
}

/**
 * Grouped category select component for hierarchical expense categories
 */
const GroupedCategorySelect: FC<IGroupedCategorySelectProps> = ({
  name,
  label,
  categories,
  id,
  required = false,
  selectedValue,
  showNoneLabel = false,
  allowCreate = false,
  isFilter = false,
  onCategoryCreated,
  ...rest
}) => {
  const { register, formState: { errors } } = useForm();
  const dispatch = useAppDispatch();
  const labelId = `${name}-label`;
  const error = get(errors, name);
  
  // State for category creation modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createType, setCreateType] = useState<'category' | 'subcategory'>('category');
  const [parentCategoryId, setParentCategoryId] = useState<string>('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Transform categories into grouped structure
  const transformCategoriesToGroups = (categories: ExpenseCategory[]): ICategoryGroup[] => {
    // console.log('Raw categories:', categories);
    
    const groups: ICategoryGroup[] = [];
    const processedCategoryIds = new Set<string>();
    
    // Build a map of all categories by ID for quick lookup
    const categoryMap = new Map<string, ExpenseCategory>();
    categories.forEach(cat => {
      categoryMap.set(cat._id, cat);
    });
    
    // console.log('Category map size:', categoryMap.size);
    
    // Process each category to build groups
    categories.forEach(category => {
      // Skip if already processed
      if (processedCategoryIds.has(category._id)) {
        return;
      }
      
      // Check if this is a parent category (has subcategories array)
      if (category.subcategories && Array.isArray(category.subcategories) && category.subcategories.length > 0) {
        // console.log(`Found parent category: ${category.name} with ${category.subcategories.length} subcategories`);
        
        const categoryOptions: IGroupedCategoryOption[] = [
          // Add parent category as first option (marked as parent for styling)
          {
            label: `${category.label || category.name}`,
            value: category._id,
            color: category.color,
            icon: category.icon,
            description: category.description,
            isSubcategory: false
          }
        ];
        
        // Add subcategories from the subcategories array
        category.subcategories.forEach((subCat: any) => {
          // subcategory might be an ID string or an object
          let subcategoryData: ExpenseCategory | undefined;
          
          if (typeof subCat === 'string') {
            subcategoryData = categoryMap.get(subCat);
          } else if (subCat._id) {
            subcategoryData = categoryMap.get(subCat._id) || subCat;
          }
          
          if (subcategoryData) {
            categoryOptions.push({
              label: subcategoryData.label || subcategoryData.name,
              value: subcategoryData._id,
              color: subcategoryData.color,
              icon: subcategoryData.icon,
              description: subcategoryData.description,
              isSubcategory: true,
              parentCategory: category.name
            });
            processedCategoryIds.add(subcategoryData._id);
          }
        });

        groups.push({
          groupName: category.name,
          color: category.color,
          categories: categoryOptions
        });
        
        processedCategoryIds.add(category._id);
      }
    });
    
    // Handle standalone categories (no parent and no subcategories)
    const standaloneCategories = categories.filter(cat => 
      !processedCategoryIds.has(cat._id) && 
      !cat.parentCategory &&
      (!cat.subcategories || cat.subcategories.length === 0)
    );

    // console.log('Standalone categories:', standaloneCategories.map(c => c.name));
    // console.log('Total groups before standalone:', groups.length);

    if (standaloneCategories.length > 0) {
      groups.push({
        groupName: 'Other Categories',
        categories: standaloneCategories.map(cat => ({
          label: cat.label || cat.name,
          value: cat._id,
          color: cat.color,
          icon: cat.icon,
          description: cat.description,
          isSubcategory: false
        }))
      });
    }

    // console.log('Final groups:', groups);
    // console.log('Processed IDs:', Array.from(processedCategoryIds));
    return groups;
  };

  const categoryGroups = transformCategoriesToGroups(categories);

  // Find selected category for display styling
  const findSelectedCategory = (value: string) => {
    for (const group of categoryGroups) {
      const found = group.categories.find(cat => cat.value === value);
      if (found) return found;
    }
    return null;
  };

  // Handle category creation
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    setIsCreating(true);
    try {
      // Prepare category data
      const categoryData = {
        name: newCategoryName,
        description: newCategoryDescription || undefined,
        parentCategory: createType === 'subcategory' && parentCategoryId ? parentCategoryId : undefined,
        color: '#6366f1', // Default color, can be customized later
        icon: createType === 'category' ? 'category' : 'subdirectory', // Default icons
        sortOrder: 999 // Default sort order
      };
      
      // Call API to create category
      const resultAction = await dispatch(createExpenseCategory(categoryData));
      
      if (createExpenseCategory.fulfilled.match(resultAction)) {
        const newCategory = resultAction.payload;
        
        // Close modal and reset form
        setCreateModalOpen(false);
        setNewCategoryName('');
        setNewCategoryDescription('');
        setIsCreating(false);
        
        // Call callback if provided
        if (onCategoryCreated) {
          onCategoryCreated(newCategory);
        }
      } else {
        // Handle error
        console.error('Failed to create category:', resultAction.error);
        setIsCreating(false);
      }
    } catch (error) {
      console.error('Error creating category:', error);
      setIsCreating(false);
    }
  };

  return (
    <>
      <FormControl fullWidth variant="outlined" error={!isEmpty(error)}>
        <InputLabel id={labelId}>{label}</InputLabel>
        <Select
          {...register(name)}
          {...rest}
          required={required}
          labelId={labelId}
          id={id}
          label={label}
          value={selectedValue || ''}
          renderValue={(selected) => {
            if (!selected || (isFilter && selected === 'all')) return isFilter && showNoneLabel ? 'All Categories' : '';
            const category = findSelectedCategory(selected as string);
            if (!category) return selected;
            
            return (
              <Box display="flex" alignItems="center" gap={1}>
                {category.color && (
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: category.color,
                      flexShrink: 0
                    }}
                  />
                )}
                <span>{category.label}</span>
                {category.isSubcategory && (
                  <Chip 
                    label={category.parentCategory} 
                    size="small" 
                    variant="outlined"
                    sx={{ height: 16, fontSize: '10px' }}
                  />
                )}
              </Box>
            );
          }}
        >
          {showNoneLabel && (
            <MenuItem value={isFilter ? 'all' : ''}>
              <em>{isFilter ? 'All Categories' : 'None'}</em>
            </MenuItem>
          )}
          
          {allowCreate && (
            <>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setCreateType('category');
                  setParentCategoryId('');
                  setCreateModalOpen(true);
                }}
                sx={{ 
                  color: 'primary.main', 
                  fontWeight: 600,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  mb: 1
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <AddIcon fontSize="small" />
                  <CategoryIcon fontSize="small" />
                  <Typography variant="body2">Create New Category</Typography>
                </Box>
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
            </>
          )}
          
          {categoryGroups.map((group, groupIndex) => [
            ...group.categories.map((category) => (
              <MenuItem
                key={category.value}
                value={category.value}
                sx={{
                  paddingLeft: category.isSubcategory ? 4 : 2,
                  fontStyle: category.isSubcategory ? 'italic' : 'normal',
                  fontWeight: !category.isSubcategory ? 600 : 400,
                  backgroundColor: !category.isSubcategory ? 'rgba(0,0,0,0.02)' : 'transparent'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} width="100%">
                  {category.color && (
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: category.color,
                        flexShrink: 0
                      }}
                    />
                  )}
                  <Box flex={1}>
                    <Box sx={{ fontWeight: category.isSubcategory ? 400 : 500 }}>
                      {category.label}
                    </Box>
                    {/* {(!category.isSubcategory && category.description) && (
                      <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {category.description}
                      </Box>
                    )} */}
                  </Box>
                  {category.isSubcategory && (
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      subcategory
                    </Box>
                  )}
                </Box>
              </MenuItem>
            )),
            // Add "Create Subcategory" option for each group if allowCreate is true
            ...(allowCreate ? [
              <MenuItem
                key={`create-sub-${groupIndex}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCreateType('subcategory');
                  setParentCategoryId(group.categories.find(c => !c.isSubcategory)?.value || '');
                  setCreateModalOpen(true);
                }}
                sx={{ 
                  color: 'secondary.main', 
                  fontStyle: 'italic',
                  paddingLeft: 4,
                  borderTop: '1px dashed',
                  borderColor: 'divider',
                  mt: 0.5
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <AddIcon fontSize="small" />
                  <SubCategoryIcon fontSize="small" />
                  <Typography variant="body2">Add Subcategory to {group.groupName}</Typography>
                </Box>
              </MenuItem>
            ] : [])
          ].flat())}
        </Select>
        {!isEmpty(error) && <FormHelperText>{error?.message ?? ''}</FormHelperText>}
      </FormControl>
      
      {/* Category Creation Modal */}
      {allowCreate && (
        <Dialog 
          open={createModalOpen} 
          onClose={() => setCreateModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              {createType === 'category' ? <CategoryIcon /> : <SubCategoryIcon />}
              <Typography variant="h6">
                Create New {createType === 'category' ? 'Category' : 'Subcategory'}
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            {createType === 'subcategory' && parentCategoryId && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Creating subcategory under: {categories.find(c => c._id === parentCategoryId)?.name}
              </Alert>
            )}
            
            <TextField
              fullWidth
              label={`${createType === 'category' ? 'Category' : 'Subcategory'} Name`}
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              margin="normal"
              required
              autoFocus
            />
            
            <TextField
              fullWidth
              label="Description (Optional)"
              value={newCategoryDescription}
              onChange={(e) => setNewCategoryDescription(e.target.value)}
              margin="normal"
              multiline
              rows={2}
            />
          </DialogContent>
          
          <DialogActions>
            <Button 
              onClick={() => {
                setCreateModalOpen(false);
                setNewCategoryName('');
                setNewCategoryDescription('');
              }}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCategory}
              disabled={!newCategoryName.trim() || isCreating}
              variant="contained"
              startIcon={createType === 'category' ? <CategoryIcon /> : <SubCategoryIcon />}
            >
              {isCreating ? 'Creating...' : `Create ${createType === 'category' ? 'Category' : 'Subcategory'}`}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default GroupedCategorySelect;